import { computed } from 'mobx';
import { computedFn } from 'mobx-utils';
import { assertNever } from 'owa-assert';
import { getDensityModeString } from 'owa-fabric-theme';
import { isFeatureEnabled } from 'owa-feature-flags';
import { getPalette } from 'owa-theme';
import type { IButtonStyles } from '@fluentui/react/lib/Button';

type DensityMode = 'compact' | 'medium' | 'full';
const getRibbonDensityModeString = (): DensityMode => {
    if (isFeatureEnabled('mon-densities')) {
        const densityModeString = getDensityModeString();
        // Since getDensityModeString returns type 'string', provide extra type validation
        if (
            densityModeString === 'compact' ||
            densityModeString === 'medium' ||
            densityModeString === 'full'
        ) {
            return densityModeString;
        }
    }

    return 'medium';
};

interface RibbonControlConstants {
    rootHeight: number;
    rootMarginVertical: number;
    rootMarginRight: number;

    borderRadius: number;

    labelFontSize: number;
    labelLineHeight: number;
    labelMarginLeft: number;

    iconSize: number;

    splitButtonMenuButtonWidth: number;
    splitButtonMenuIconSize: number;
    splitButtonMenuIconMarginVertical: number;
    splitButtonMenuIconMarginHorizontal: number;

    leftMarginBetweenThisControlAndNext: number;
}

/**
 * The following variables are used to calculate the right margin in SLR Hero App Split Button.
 * @param isReadingPanePositionRight - Whether the reading pane is on the right side of the screen.
 * @param shouldShowFolderPanel - Whether the folder panel is shown.
 * @param listViewWidth - The width of the list view.
 * @param leftNavWidth - The width of the left nav.
 */
export interface HeroAppSplitButtonRightMarginConstants {
    isReadingPanePositionRight: boolean;
    shouldShowFolderPane: boolean;
    listViewWidth: number /* getListViewDimensions().listViewWidth */;
    leftNavWidth: number;
}

const calculateRightMarginConstantsForHeroSplitButton = (
    isToolbarComposeStyle: boolean,
    rightMarginConstants?: HeroAppSplitButtonRightMarginConstants
) => {
    if (
        !isToolbarComposeStyle ||
        !rightMarginConstants ||
        !rightMarginConstants.isReadingPanePositionRight
    ) {
        return 26 /* defaultOffset */;
    }

    const hasCollapsiblePane = isFeatureEnabled('mon-tri-collapsibleFolderPane');
    const hamburgerButtonWidth: number = hasCollapsiblePane ? 0 : 48;
    const collapsiblePaneWidth: number = hasCollapsiblePane ? 40 : 0;
    const newMsgBtnWidth: number = 140; // If we want better (e.g. account for localized strings), we need a different way
    const commandBarPadding: number =
        rightMarginConstants.leftNavWidth +
        rightMarginConstants.listViewWidth -
        newMsgBtnWidth -
        hamburgerButtonWidth +
        (!hasCollapsiblePane || rightMarginConstants.shouldShowFolderPane
            ? 0
            : collapsiblePaneWidth);

    return commandBarPadding;
};

const getButtonRootConstants = (
    densityMode: DensityMode,
    isHeroStyle?: boolean,
    isToolbarComposeStyle?: boolean,
    rightMarginConstants?: HeroAppSplitButtonRightMarginConstants
): RibbonControlConstants => {
    if (densityMode === 'compact') {
        return {
            rootHeight: 24,

            // Future: an API should allow us to control RibbonBottomBar's height
            // and then vertical margins would be reduced/removed.
            rootMarginVertical: 8, // (40 [RibbonBottomBarHeight] - 24 [height]) / 2
            rootMarginRight: 0,
            borderRadius: 4,
            labelFontSize: 12,
            labelLineHeight: 14.06,
            labelMarginLeft: isHeroStyle ? 5 : 4,
            iconSize: 16,
            splitButtonMenuButtonWidth: isHeroStyle ? 28 : 12,
            splitButtonMenuIconSize: 12,
            splitButtonMenuIconMarginVertical: 0,
            splitButtonMenuIconMarginHorizontal: isHeroStyle ? 6 : 0,
            leftMarginBetweenThisControlAndNext: isHeroStyle ? 7 : 0,
        };
    }

    if (densityMode === 'medium' || densityMode === 'full') {
        return {
            rootHeight: 32,

            // Future: an API should allow us to control RibbonBottomBar's height
            // and then vertical margins would be reduced/removed.
            rootMarginVertical: 4, // (40 [RibbonBottomBarHeight] - 32 [height]) / 2
            rootMarginRight: 0,
            borderRadius: 4,
            labelFontSize: 14,
            labelLineHeight: 16.41,
            labelMarginLeft: isHeroStyle ? 10 : 4,
            iconSize: 20,
            splitButtonMenuButtonWidth: isHeroStyle ? 28 : 16,
            splitButtonMenuIconSize: isHeroStyle ? 16 : 12,
            splitButtonMenuIconMarginVertical: 0,
            splitButtonMenuIconMarginHorizontal: isHeroStyle ? 6 : 2,
            leftMarginBetweenThisControlAndNext: isHeroStyle ? 7 : 0,
        };
    } else {
        assertNever(densityMode);
    }
};

const getButtonLabelConstants = (densityMode: DensityMode, isHeroStyle: boolean) => {
    const constants = getButtonRootConstants(densityMode, isHeroStyle);
    return {
        fontSize: constants.labelFontSize,
        lineHeight: constants.labelLineHeight,
        marginLeft: constants.labelMarginLeft,
    };
};

const getButtonIconConstants = (densityMode: DensityMode) => {
    const constants = getButtonRootConstants(densityMode);
    return {
        width: constants.iconSize,
        height: constants.iconSize,
    };
};

const slrSplitButtonDividerWidth = 1;

const getSlrAppButtonConstants = (densityMode: DensityMode) => {
    const constants = getButtonRootConstants(densityMode, false /* isHeroStyle */);
    return {
        height: constants.rootHeight,
        marginTop: constants.rootMarginVertical,
        marginBottom: constants.rootMarginVertical,
        marginRight: constants.rootMarginRight,
        borderRadius: constants.borderRadius,
    };
};

const getSlrAppSplitButtonConstants = (densityMode: DensityMode, isHeroStyle: boolean) => {
    const constants = getButtonRootConstants(densityMode, isHeroStyle);
    return {
        height: constants.rootHeight,
        marginTop: constants.rootMarginVertical,
        marginBottom: constants.rootMarginVertical,
        marginRight: constants.rootMarginRight,
        paddingRight: 12,
        borderWidth: 0,
        borderTopLeftRadius: constants.borderRadius,
        borderBottomLeftRadius: constants.borderRadius,
    };
};

const getSlrAppSplitButtonMenuButtonConstants = (
    densityMode: DensityMode,
    isHeroStyle: boolean
) => {
    const constants = getButtonRootConstants(densityMode, isHeroStyle);
    return {
        height: constants.rootHeight,
        width: constants.splitButtonMenuButtonWidth,
        marginTop: constants.rootMarginVertical,
        marginBottom: constants.rootMarginVertical,
        borderWidth: 0,
        borderTopRightRadius: 4,
        borderBottomRightRadius: 4,
    };
};

const getSplitButtonMenuIconConstants = (densityMode: DensityMode, isHeroStyle: boolean) => {
    const constants = getButtonRootConstants(densityMode, isHeroStyle);
    return {
        width: constants.splitButtonMenuIconSize,
        height: constants.splitButtonMenuIconSize,
        marginLeft: constants.splitButtonMenuIconMarginHorizontal,
        marginRight: constants.splitButtonMenuIconMarginHorizontal,
        marginTop: constants.splitButtonMenuIconMarginVertical,
        marginBottom: constants.splitButtonMenuIconMarginVertical,
    };
};

/**
 * Get button styles for SLR Hero App Split Button.
 * @param isToolbarComposeStyle - Whether we're styling for toolbar compose mode.
 * @param rightMarginBetweenThisControlAndNext - The margin between this hero button and the next control.
 */
export const getSlrHeroAppSplitButtonStyles = computedFn(
    (
        isToolbarComposeStyle: boolean,
        rightMarginConstants: HeroAppSplitButtonRightMarginConstants | undefined
    ): IButtonStyles => {
        const densityMode = getRibbonDensityModeString();
        const palette = getPalette();
        const constants = getButtonRootConstants(
            densityMode,
            true /* isHeroStyle */,
            isToolbarComposeStyle
        );
        const splitButtonContainerRightMargin = calculateRightMarginConstantsForHeroSplitButton(
            isToolbarComposeStyle,
            rightMarginConstants
        );
        const slrAppSplitButtonConstants = getSlrAppSplitButtonConstants(
            densityMode,
            true /* isHeroStyle */
        );
        const slrAppSplitButtonMenuButtonConstants = getSlrAppSplitButtonMenuButtonConstants(
            densityMode,
            true /* isHeroStyle */
        );

        const style: IButtonStyles = {
            root: {
                ...slrAppSplitButtonConstants,
                backgroundColor: palette.themePrimary,
                color: palette.white,

                selectors: {
                    ':hover': {
                        ...slrAppSplitButtonConstants,
                        backgroundColor: palette.themeDark,
                        color: palette.white,
                    },
                    ':active': {
                        ...slrAppSplitButtonConstants,
                        backgroundColor: palette.themeDarker,
                        color: palette.white,
                    },
                },
            },
            rootDisabled: {
                ...slrAppSplitButtonConstants,
                backgroundColor: palette.themeLighter,
                color: palette.white,
            },
            label: getButtonLabelConstants(densityMode, true /* isHeroStyle */),
            icon: getButtonIconConstants(densityMode),
            splitButtonContainer: {
                marginLeft: isFeatureEnabled('mon-tri-collapsibleFolderPane')
                    ? constants.leftMarginBetweenThisControlAndNext
                    : 0,
                marginRight: splitButtonContainerRightMargin,
            },
            splitButtonMenuButton: {
                ...slrAppSplitButtonMenuButtonConstants,
                backgroundColor: palette.themePrimary,
                color: palette.white,

                selectors: {
                    ':hover': {
                        ...slrAppSplitButtonMenuButtonConstants,
                        backgroundColor: palette.themeDark,
                        color: palette.white,
                    },
                    ':active': {
                        ...slrAppSplitButtonMenuButtonConstants,
                        backgroundColor: palette.themeDarker,
                        color: palette.white,
                    },
                },
            },
            splitButtonMenuButtonDisabled: {
                ...slrAppSplitButtonConstants,
                backgroundColor: palette.themeLighter,
                color: palette.white,
            },
            splitButtonMenuButtonExpanded: {
                ...slrAppSplitButtonMenuButtonConstants,
                backgroundColor: palette.themeDark,
                color: palette.white,

                selectors: {
                    ':hover': {
                        ...slrAppSplitButtonMenuButtonConstants,
                        backgroundColor: palette.themeDark,
                        color: palette.white,
                    },
                },
            },
            splitButtonMenuIcon: getSplitButtonMenuIconConstants(
                densityMode,
                true /* isHeroStyle */
            ),
            splitButtonDivider: {
                width: slrSplitButtonDividerWidth,
                height: '100%',
                position: 'absolute',
                right: constants.splitButtonMenuButtonWidth + slrSplitButtonDividerWidth - 1,
                backgroundColor: palette.neutralLighterAlt,
            },
        };

        return style;
    }
);

const computeDefaultSlrAppRibbonButtonStyles = computed(
    (): IButtonStyles => {
        const densityMode = getRibbonDensityModeString();

        const style: IButtonStyles = {
            root: getSlrAppButtonConstants(densityMode),
            label: getButtonLabelConstants(densityMode, false /* isHeroStyle */),
            icon: getButtonIconConstants(densityMode),
            splitButtonMenuButton: getSlrAppSplitButtonMenuButtonConstants(
                densityMode,
                false /* isHeroStyle */
            ),
            splitButtonMenuIcon: getSplitButtonMenuIconConstants(
                densityMode,
                false /* isHeroStyle */
            ),
        };

        return style;
    }
);

export const getDefaultSlrAppRibbonButtonStyles = () =>
    computeDefaultSlrAppRibbonButtonStyles.get();
